<template>
  <div>
    <h2>Verschiebbare Stunden</h2>
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>

    <b-row class="mb-3" align-h="between">
      <b-col cols="auto">
        <b-form-input
          v-model="filter"
          type="text"
          placeholder="Einträge Filtern..."
          class="mb-2"
        />
      </b-col>
      <b-col cols="auto">
        <b-button
          variant="primary"
          :disabled="selectedWorklogIds.size === 0"
          @click="showMoveModal = true"
        >
          <b-icon icon="arrow-right-circle" />
          Verschieben ({{ selectedWorklogIds.size }})
        </b-button>
      </b-col>
    </b-row>

    <div>
      <b-table
        striped
        hover
        :items="filteredWorklogs"
        :fields="fields"
        :select-mode="'multi'"
        selectable
        @row-selected="onRowSelected"
        :selected="selectedWorklogsForTable"
      >
        <template v-slot:cell(selected)="{ rowSelected, item }">
          <b-form-checkbox
            :checked="isWorklogSelected(item.id)"
            @change="toggleRowSelection($event, item)"
          />
        </template>
        <template v-slot:cell(username)="data">
          {{ data.item.username }}
        </template>
        <template v-slot:cell(parentTaskId)="data">
          {{ data.item.parentTaskId || '-' }}
        </template>
        <template v-slot:cell(clickUpCustomTaskId)="data">
          {{ data.item.clickUpCustomTaskId || '-' }}
        </template>
        <template v-slot:cell(accountingTaskName)="data">
          {{ getAccountingTaskName(data.item.projectAccountingTaskId) || '-' }}
        </template>
        <template v-slot:cell(taskName)="data">
          {{ data.item.taskName || '-' }}
        </template>
        <template v-slot:cell(comment)="data">
          {{ data.item.comment || '-' }}
        </template>
        <template v-slot:cell(start)="data">
          {{ formatDate(data.item.start) }}
        </template>
        <template v-slot:cell(timeSpendInMinutes)="data">
          {{ formatMinutesToHoursMinutes(data.item.timeSpendInMinutes) }}
        </template>
      </b-table>
    </div>

    <!-- Move Modal -->
    <b-modal
      v-model="showMoveModal"
      title="Stunden verschieben"
      @ok="handleMove"
      @cancel="resetMoveModal"
      ok-title="Verschieben"
      cancel-title="Abbrechen"
    >
      <div v-if="moving" class="text-center">
        <div class="loading-spinner"></div>
        <p>Verschiebe Stunden...</p>
      </div>
      <div v-else>
        <p>
          <strong>{{ selectedWorklogIds.size }}</strong> Stunde(n) werden in das folgende Projekt verschoben:
        </p>
        <b-form-group label="Zielprojekt">
          <b-form-select
            v-model="selectedTargetProjectId"
            :options="projectOptions"
            :state="selectedTargetProjectId ? null : false"
            required
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Bitte wählen Sie ein Projekt
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-alert v-if="moveError" variant="danger" show>
          {{ moveError }}
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { STOREMODULE_ENDPOINT_MAP, STOREMODULE_ENDPOINTMODULE_MAP } from '@/constants'
import http from '@/$plugins/http'

export default {
  name: 'Projects.Worklogs.Move',
  data () {
    return {
      loading: false,
      moving: false,
      worklogs: [],
      selectedWorklogIds: new Set(),
      filter: '',
      showMoveModal: false,
      selectedTargetProjectId: null,
      projects: [],
      clients: [],
      projectAccountingTasks: [],
      accountingTasks: [],
      moveError: null,
      fields: [
        { key: 'selected', label: 'Verschieben', sortable: false, thStyle: { width: '50px' } },
        { key: 'username', label: 'Benutzer', sortable: true },
        { key: 'parentTaskId', label: 'Parent Task-Id', sortable: true },
        { key: 'clickUpCustomTaskId', label: 'Task-Id', sortable: true },
        { key: 'accountingTaskName', label: 'Abrechnungs-Task', sortable: true },
        { key: 'taskName', label: 'Task-Name', sortable: true },
        { key: 'comment', label: 'Kommentar', sortable: true },
        { key: 'start', label: 'Datum', sortable: true },
        { key: 'timeSpendInMinutes', label: 'Zeitaufwand', sortable: true }
      ]
    }
  },
  computed: {
    projectId () {
      const urlParts = window.location.pathname.split('/')
      return urlParts[urlParts.length - 2]
    },
    filteredWorklogs () {
      if (!this.filter) {
        return this.worklogs
      }
      const filterLower = this.filter.toLowerCase()
      return this.worklogs.filter(worklog => {
        return (
          (worklog.username && worklog.username.toLowerCase().includes(filterLower)) ||
          (worklog.taskName && worklog.taskName.toLowerCase().includes(filterLower)) ||
          (worklog.comment && worklog.comment.toLowerCase().includes(filterLower)) ||
          (worklog.clickUpCustomTaskId && worklog.clickUpCustomTaskId.toLowerCase().includes(filterLower)) ||
          (worklog.parentTaskId && worklog.parentTaskId.toLowerCase().includes(filterLower))
        )
      })
    },
    selectedWorklogIdsArray () {
      return Array.from(this.selectedWorklogIds)
    },
    selectedWorklogsForTable () {
      // Return worklogs that are currently selected (for b-table's selected prop)
      return this.filteredWorklogs.filter(w => this.selectedWorklogIds.has(w.id))
    },
    projectOptions () {
      return this.projects
        .filter(p => {
          const projectId = p.id?.value || p.id
          return projectId !== this.projectId
        })
        .map(p => {
          const projectId = p.id?.value || p.id
          const projectName = p.name || p.value?.name || projectId
          const clientId = p.clientId || p.value?.clientId
          const clientName = this.getClientName(clientId)
          const displayText = clientName ? `${projectName} (${clientName})` : projectName
          return {
            value: projectId,
            text: displayText
          }
        })
    }
  },
  methods: {
    isWorklogSelected (worklogId) {
      return this.selectedWorklogIds.has(worklogId)
    },
    onRowSelected (items) {
      // Update the Set based on the selected items
      const selectedIds = new Set(items.map(item => item.id))
      this.selectedWorklogIds = selectedIds
    },
    toggleRowSelection (checked, item) {
      if (checked) {
        this.selectedWorklogIds.add(item.id)
      } else {
        this.selectedWorklogIds.delete(item.id)
      }
      // Create a new Set to trigger reactivity
      this.selectedWorklogIds = new Set(this.selectedWorklogIds)
    },
    formatDate (date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString('de-CH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatMinutesToHoursMinutes (minutes) {
      if (!minutes) return '-'
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return `${hours}h ${remainingMinutes}m`
    },
    getAccountingTaskName (projectAccountingTaskId) {
      if (!projectAccountingTaskId) return null
      const projectAccountingTask = this.projectAccountingTasks.find(
        pat => pat.id === projectAccountingTaskId
      )
      if (!projectAccountingTask) return null
      const accountingTask = this.accountingTasks.find(
        at => at.id === projectAccountingTask.accountingTaskId
      )
      return accountingTask ? accountingTask.name : null
    },
    getClientName (clientId) {
      if (!clientId) return null
      const client = this.clients.find(c => {
        const cId = c.id?.value || c.id
        return cId === clientId
      })
      if (!client) return null
      return client.name || client.value?.name || null
    },
    async loadWorklogs () {
      try {
        this.loading = true
        const response = await http.get(
          `/${STOREMODULE_ENDPOINTMODULE_MAP.business}/api/${STOREMODULE_ENDPOINT_MAP.worklogs}/fromproject/${this.projectId}/moveable`
        )
        this.worklogs = response.data.result || response.data || []
      } catch (error) {
        console.error('Error loading worklogs:', error)
        this.$bvToast.toast('Fehler beim Laden der Stunden', {
          title: 'Fehler',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    async loadProjects () {
      try {
        const response = await http.get(
          `/${STOREMODULE_ENDPOINTMODULE_MAP.business}/api/${STOREMODULE_ENDPOINT_MAP.projects}/allactives`
        )
        const projects = response.data.result || response.data || []
        // Extract value if it's wrapped in ValueModelWithChangeRequests
        this.projects = projects.map(p => p.value || p)
      } catch (error) {
        console.error('Error loading projects:', error)
      }
    },
    async loadClients () {
      try {
        // Load clients - try the standard endpoint first
        const response = await http.get(
          `/${STOREMODULE_ENDPOINTMODULE_MAP.business}/api/${STOREMODULE_ENDPOINT_MAP.clients}`
        )
        const clients = response.data.result || response.data || []
        // Extract value if it's wrapped in ValueModelWithChangeRequests
        this.clients = clients.map(c => c.value || c)
      } catch (error) {
        console.error('Error loading clients:', error)
        // If the endpoint doesn't exist, we'll just continue without client names
        this.clients = []
      }
    },
    async loadProjectAccountingTasks () {
      try {
        const response = await http.get(
          `/${STOREMODULE_ENDPOINTMODULE_MAP.business}/api/${STOREMODULE_ENDPOINT_MAP.projectaccountingtasks}/fromproject/${this.projectId}/all`
        )
        this.projectAccountingTasks = response.data.result || response.data || []
      } catch (error) {
        console.error('Error loading project accounting tasks:', error)
      }
    },
    async loadAccountingTasks () {
      try {
        const response = await http.get(
          `/${STOREMODULE_ENDPOINTMODULE_MAP.business}/api/${STOREMODULE_ENDPOINT_MAP.accountingtasks}/all`
        )
        this.accountingTasks = response.data.result || response.data || []
      } catch (error) {
        console.error('Error loading accounting tasks:', error)
      }
    },
    async handleMove () {
      if (!this.selectedTargetProjectId) {
        this.moveError = 'Bitte wählen Sie ein Zielprojekt aus'
        return
      }

      if (this.selectedWorklogIds.size === 0) {
        this.moveError = 'Bitte wählen Sie mindestens eine Stunde aus'
        return
      }

      try {
        this.moving = true
        this.moveError = null

        await http.put(
          `/${STOREMODULE_ENDPOINTMODULE_MAP.business}/api/${STOREMODULE_ENDPOINT_MAP.worklogs}/moveworklogstoproject/${this.selectedTargetProjectId}`,
          {
            worklogIds: this.selectedWorklogIdsArray
          }
        )

        this.$bvToast.toast(
          `${this.selectedWorklogIds.size} Stunde(n) erfolgreich verschoben`,
          {
            title: 'Erfolg',
            variant: 'success',
            solid: true
          }
        )

        this.resetMoveModal()
        await this.loadWorklogs()
        this.selectedWorklogIds = new Set()
      } catch (error) {
        console.error('Error moving worklogs:', error)
        this.moveError =
          error.response?.data?.message ||
          error.message ||
          'Fehler beim Verschieben der Stunden'
      } finally {
        this.moving = false
      }
    },
    resetMoveModal () {
      this.selectedTargetProjectId = null
      this.moveError = null
      this.showMoveModal = false
    }
  },
  async created () {
    await Promise.all([
      this.loadWorklogs(),
      this.loadProjects(),
      this.loadClients(),
      this.loadProjectAccountingTasks(),
      this.loadAccountingTasks()
    ])
  }
}
</script>

<style lang="scss" scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
